import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { capitalize } from 'lodash';
import { useSelector } from 'react-redux';

import { EditFamilyMemberDrawer } from '../../EditFamilyMemberDrawer';
import { ErrorResponseDto, ResponseDto } from 'theSource/types';
import { useApiMutation } from 'theSource/utils';
import {
  GET_DEPENDENTS_QUERY_KEY,
  REMOVE_DEPENDENT_QUERY_KEY,
} from 'enrollment/queries/useEnrollmentFamilyQuery';
import {
  useCompanySettingsQuery,
  useConfigurationQuery,
} from 'theSource/queries';
import { FamilyMemberCard } from '../FamilyMemberCard';
import { Loader } from 'theSource/components';
import { PSModalWrapper } from 'enrollment/components/Shared/Modal';
import { PsButtonTuner } from 'stencilSetup';
import { useRestrictionsQuery } from 'enrollment/queries/useRestrictionsQuery';
import {
  selectEnrollmentOrgLifeEventId,
  selectEnrollmentOrgPlanId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { BenefitRestrictions } from 'enrollment/queries/useRestrictionsQuery/types';
import { useBenefitCategoryQuery } from 'enrollment/queries';
import {
  BenefitDto,
  DependentDto,
} from 'enrollment/queries/useBenefitCategoryQuery/types';

export interface FamilyMemberDetailsProps {
  enrollmentContextType?: string | null;
  data?: object[];
  dependentsData?: any;
  children?: JSX.Element;
  setCanDecrease?: Dispatch<SetStateAction<boolean>>;
}

export const FamilyMemberDetails = ({
  enrollmentContextType,
  data,
  dependentsData,
  children,
  setCanDecrease,
}: FamilyMemberDetailsProps) => {
  const { t } = useTranslation('pages.enrollmentFamily');
  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);
  const orgLifeEventID = useSelector(selectEnrollmentOrgLifeEventId);

  const [dependents, setDependents] = useState(data);
  const [dependentId, setDependentId] = useState<string>('');
  const [depPublicId, setDepPublicId] = useState<string>('');
  const [openFamilyDetailsDrawer, setOpenAddFamilyDetailsDrawer] =
    useState<boolean>(false);
  const [dependentIdForEdit, setDependentIdForEdit] = useState<string>('');

  const { configuration } = useConfigurationQuery();
  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });

  const { data: restrictions } = useRestrictionsQuery({
    orgLifeEventID: orgLifeEventID,
    orgPlanYearID: orgPlanYearId,
    enabled: orgLifeEventID > 0 && orgPlanYearId > 0,
  });

  // using init because we only care about initial coverage data
  const { data: benefitPlans } = useBenefitCategoryQuery({
    key: `init-benefits-${enrollmentContextType}`,
    enrollmentContextType: enrollmentContextType || '',
    enabled: orgLifeEventID > 0 && orgPlanYearId > 0,
    lumosApiHost: String(configuration?.apiHost),
    includeEligibleDependentsData: true,
  });

  const cannotRemoveDependents = new Set<number>();
  if (restrictions && enrollmentContextType === 'life_event') {
    //if any of the restrictions.data has a decrease value of false and the dependent is covered by that plan, then canDecrease is false

    restrictions.data.forEach((restriction: BenefitRestrictions) => {
      if (!restriction.decrease) {
        benefitPlans?.data?.benefits
          .find(
            (benefit: BenefitDto) =>
              benefit.benefit.org_benefit_id === restriction.org_benefit_id
          )
          ?.coverage?.req_dependents.forEach((depCov: DependentDto) => {
            cannotRemoveDependents.add(Number(depCov.dependent_id));
          });
      }
    });

    if (cannotRemoveDependents.size !== 0 && setCanDecrease) {
      setCanDecrease(false);
    }
  }

  const showDocRequestWarning =
    companySettings?.data.dependent_decision_iq_enabled ||
    companySettings?.data.subscriber_document_management_enabled;

  const lumosApiHost = configuration?.apiHost;

  const { isLoading, mutate: removeDependent } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    {}
  >({
    queryKey: REMOVE_DEPENDENT_QUERY_KEY,
    queryKeysToInvalidate: `${GET_DEPENDENTS_QUERY_KEY}-${enrollmentContextType}`,
    method: 'delete',
    endpoint: `${lumosApiHost}/v2/self_service/dependents/profile/${dependentId}/${enrollmentContextType}?public_id=${depPublicId}&life_event_completed=false`,
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const handleDelete = (e: any) => {
    const elementId = e?.currentTarget?.id.split('_')[0];
    const depId = elementId.split('-')[1] ? elementId.split('-')[1] : '';
    const publicId = elementId.split('-')[2] ? elementId.split('-')[2] : '';
    setDependentId(depId);
    setDepPublicId(publicId);
  };

  const handleUpdatedData = (data: any) => {
    setDependents(data);
  };

  useEffect(() => {
    document
      .querySelectorAll('button[id^="removeDependent"][id$="_primary"]')
      .forEach((modalSaveBtn) => {
        modalSaveBtn.addEventListener('click', (e: Event) => handleDelete(e));
      });
    setDependents(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dependentId !== '' && depPublicId !== '') {
      removeDependent({});
    }
  }, [dependentId, depPublicId, removeDependent]);

  const handleEditFamilyMember = (dependent: any) => {
    setOpenAddFamilyDetailsDrawer(true);
    setDependentIdForEdit(dependent.id);
  };

  return (
    <>
      {isLoading ? (
        <div className="mt-5">
          <Loader />
        </div>
      ) : (
        <>
          <Grid
            container
            spacing={4}
            className="row mx-auto mb-4 w-100"
            sx={{ marginTop: '40px' }}
            data-testid="enrollment-family-member"
          >
            {dependents?.map((dependent: any, index: number) => (
              <FamilyMemberCard dependent={dependent} key={index}>
                <div className="mt-3 d-flex justify-content-between">
                  <PsButtonTuner
                    buttonId={`removeDependent-${dependent.id}-btn`}
                    label={t('removeButtonLabel')}
                    level="tertiary"
                    icon="trash"
                    otherData={[
                      { attribute: 'data-bs-toggle', value: 'modal' },
                      {
                        attribute: 'data-bs-target',
                        value: dependent?.qmcso
                          ? `#cannotRemoveDependent-${dependent.id}-${dependent.public_id}`
                          : `#removeDependent-${dependent.id}-${dependent.public_id}`,
                      },
                      {
                        attribute: 'data-testid',
                        value: `removeDependent-${dependent.id}-btn`,
                      },
                      {
                        attribute: 'aria-describedby',
                        value: `${dependent.id}-name`,
                      },
                    ]}
                    {...(cannotRemoveDependents.has(Number(dependent.id))
                      ? { disabled: true }
                      : {})}
                  />
                  {!dependent?.qmcso && (
                    <PSModalWrapper
                      id={`removeDependent-${dependent.id}-${dependent.public_id}`}
                      message={
                        showDocRequestWarning
                          ? t('removeDependentModalMessageWithDocWarning', {
                              dependent_name: capitalize(dependent.first_name),
                            })
                          : t('removeDependentModalMessage', {
                              dependent_name: capitalize(dependent.first_name),
                            })
                      }
                      title={t('removeDependentModalTitle', {
                        dependent_name: capitalize(dependent.first_name),
                      })}
                      primaryButton={t('confirmButtonLabel')}
                      primaryActive={true}
                      secondaryButton={t('cancelButtonLabel')}
                      extraClass="text-left"
                    ></PSModalWrapper>
                  )}
                  {dependent?.qmcso && (
                    <PSModalWrapper
                      id={`cannotRemoveDependent-${dependent.id}-${dependent.public_id}`}
                      message={t('common:qmcsoCannotRemove')}
                      title={t('removeDependentModalTitle', {
                        dependent_name: capitalize(dependent.first_name),
                      })}
                      primaryButton={t('cancelButtonLabel')}
                      extraClass="text-left"
                    ></PSModalWrapper>
                  )}

                  <PsButtonTuner
                    buttonId="edit-family-member-btn"
                    label={t('editButtonLabel')}
                    level="tertiary"
                    icon="pencil-simple"
                    onClick={() => handleEditFamilyMember(dependent)}
                    data-testid="edit-family-member-btn"
                    otherData={[
                      {
                        attribute: 'aria-describedby',
                        value: `${dependent.id}-name`,
                      },
                    ]}
                  />
                </div>
              </FamilyMemberCard>
            ))}
          </Grid>
          <Grid
            container
            spacing={6}
            justifyContent={{ xs: 'center', sm: 'flex-start' }}
          >
            {children}
          </Grid>
          <EditFamilyMemberDrawer
            enrollmentContextType={enrollmentContextType}
            data={dependentsData}
            dependents={dependents}
            dependentId={dependentIdForEdit}
            updateDependentsData={handleUpdatedData}
            openFamilyDetailsDrawer={openFamilyDetailsDrawer}
            setOpenAddFamilyDetailsDrawer={setOpenAddFamilyDetailsDrawer}
          />
        </>
      )}
    </>
  );
};
